import RAPIER from "@dimforge/rapier2d-deterministic-compat";
import { generateChamber } from "./chambers";
import {
  CHAMBER_HEIGHT,
  CORE_RADIUS,
  FIXED_DT,
  MAX_PATENTS,
  PATENT_DAMAGE_INVULNERABILITY_TICKS,
  PATENT_MAX_BONUS_SCORE,
  PHYSICS,
  PULP_RADIUS,
  RULESET_VERSION,
  VIEW_HEIGHT,
  type ChamberSpec,
  type DeathCause,
  type GameEvent,
  type GameSnapshot,
  type HazardSpec,
  type Material,
  type RenderHazard,
  type RenderPowerup,
  type RunMode,
  type RunState,
  type SegmentSpec,
} from "./constants";
import { WALL_LEFT, WALL_RIGHT } from "./constants";
import { createScoreState, resetCombo, scoreDepth, scoreMultiplierForPatents, scorePlow } from "./scoring";
import { ENEMY_SPECS } from "./enemies";

interface SurfaceRuntime {
  spec: SegmentSpec;
  collider: RAPIER.Collider;
}

interface ChamberRuntime {
  spec: ChamberSpec;
  surfaces: SurfaceRuntime[];
}

let physicsInitialized = false;

export async function initializePhysics(): Promise<void> {
  if (physicsInitialized) return;
  await RAPIER.init();
  physicsInitialized = true;
}

function createContinuousWalls(world: RAPIER.World): void {
  const halfHeight = 51_000;
  const centerY = 50_000;
  world.createCollider(RAPIER.ColliderDesc.cuboid(4, halfHeight).setTranslation(WALL_LEFT - 4, centerY).setFriction(0.05));
  world.createCollider(RAPIER.ColliderDesc.cuboid(4, halfHeight).setTranslation(WALL_RIGHT + 4, centerY).setFriction(0.05));
}

function colliderForSegment(segment: SegmentSpec): RAPIER.ColliderDesc {
  let ax = segment.ax;
  let bx = segment.bx;
  if (ax === WALL_LEFT) ax -= 16;
  else if (ax === WALL_RIGHT) ax += 16;
  if (bx === WALL_LEFT) bx -= 16;
  else if (bx === WALL_RIGHT) bx += 16;
  const dx = bx - ax;
  const dy = segment.by - segment.ay;
  const length = Math.hypot(dx, dy);
  return RAPIER.ColliderDesc.roundCuboid(length / 2, 2.5, 2.5)
    .setTranslation((ax + bx) / 2, (segment.ay + segment.by) / 2)
    .setRotation(Math.atan2(dy, dx))
    .setRestitution(0)
    .setFriction(0.05)
    .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);
}

function overlapsHazard(playerX: number, playerY: number, playerRadius: number, hazard: HazardSpec, renderX: number, renderY: number): boolean {
  const closestX = Math.max(renderX, Math.min(playerX, renderX + hazard.width));
  const closestY = Math.max(renderY, Math.min(playerY, renderY + hazard.height));
  const dx = playerX - closestX;
  const dy = playerY - closestY;
  return dx * dx + dy * dy <= playerRadius * playerRadius;
}

export function deathMessageForCause(cause: DeathCause): string {
  if (cause === "incompatible-machine") return "Your machinery was jammed";
  if (cause === "incompatible-produce") return "Splat!";
  if (cause === "time-collapse") return "Time collapse";
  if (cause === "timeline-corruption") return "Timeline corrupted";
  return "Run ended";
}

function hazardDeathCause(hazard: HazardSpec): DeathCause {
  return ENEMY_SPECS[hazard.sprite].category === "machine" ? "incompatible-machine" : "incompatible-produce";
}

const START_X = 180;
const START_Y = 52;
const CAMERA_TARGET_Y = 238;
const PRELOAD_CHAMBERS = 6;
const STERILIZER_START_Y = -300;
const STERILIZER_INITIAL_SPEED = 72;
const STERILIZER_MAX_SPEED = 126;
const STERILIZER_MAX_LEAD = 760;
const OVERDRIVE_TICKS = 5 * 60;
const OVERDRIVE_SPEED_MULTIPLIER = 1.35;

export function paceForDepth(depth: number): number {
  return 1 + Math.min(1.5, Math.max(0, depth) * 1.5 / 3_000);
}

export function sterilizerPaceForDepth(depth: number): number {
  return 1 + (paceForDepth(depth) - 1) * 0.5;
}

export function sterilizerSpeedForDepth(depth: number): number {
  return Math.min(STERILIZER_MAX_SPEED, STERILIZER_INITIAL_SPEED * sterilizerPaceForDepth(depth));
}

export function overdrivePaceForBase(basePace: number, active: boolean, capturedBasePace: number | null): number {
  return active ? Math.max(basePace, capturedBasePace ?? basePace) * OVERDRIVE_SPEED_MULTIPLIER : basePace;
}
export function canBreakHazard(material: Material, weakness: Material, overdriveTicks: number): boolean {
  return overdriveTicks > 0 || material === weakness;
}
export class Game {
  static readonly rulesetVersion = RULESET_VERSION;

  private world!: RAPIER.World;
  private eventQueue!: RAPIER.EventQueue;
  private tomatoBody!: RAPIER.RigidBody;
  private tomatoCollider!: RAPIER.Collider;
  private readonly chambers = new Map<number, ChamberRuntime>();
  private readonly surfaceByHandle = new Map<number, SurfaceRuntime>();
  private readonly destroyedHazards = new Set<string>();
  private readonly collectedPowerups = new Set<string>();
  private readonly missedHazards = new Set<string>();
  private readonly events: GameEvent[] = [];
  private scoreState = createScoreState(START_Y);
  private runState: RunState = "title";
  private runMode: RunMode = "normal";
  private material: Material = "pulp";
  private requestedCore = false;
  private blockedExpansion = false;
  private tickCount = 0;
  private previousCoreRequested = false;
  private pistonCooldown = 0;
  private pistonBufferTicks = 0;
  private overdriveTicks = 0;
  private preOverdrivePace: number | null = null;
  private patentCount = 0;
  private damageInvulnerabilityTicks = 0;
  private seedValue = 1;
  private eraSeedValue = 1;
  private cameraY = 0;
  private sterilizerY = STERILIZER_START_Y;
  private deathReason = "";

  constructor(seed = 1, mode: RunMode = "normal", eraSeed = seed) {
    this.reset(seed, mode, "title", eraSeed);
  }

  get state(): RunState {
    return this.runState;
  }

  get seed(): number {
    return this.seedValue;
  }

  get eraSeed(): number {
    return this.eraSeedValue;
  }

  get reason(): string {
    return this.deathReason;
  }

  start(): void {
    if (this.runState === "title") this.runState = "playing";
  }

  pause(): void {
    if (this.runState === "playing") this.runState = "paused";
  }

  resume(): void {
    if (this.runState === "paused") this.runState = "playing";
  }

  reset(seed: number, mode: RunMode = this.runMode, state: RunState = "playing", eraSeed = seed): void {
    if (this.world) this.world.free();
    this.seedValue = seed >>> 0;
    this.eraSeedValue = eraSeed >>> 0;
    this.runMode = mode;
    this.runState = state;
    this.tickCount = 0;
    this.material = "pulp";
    this.requestedCore = false;
    this.blockedExpansion = false;
    this.previousCoreRequested = false;
    this.pistonCooldown = 0;
    this.pistonBufferTicks = 0;
    this.overdriveTicks = 0;
    this.preOverdrivePace = null;
    this.patentCount = 0;
    this.damageInvulnerabilityTicks = 0;
    this.cameraY = 0;
    this.sterilizerY = STERILIZER_START_Y;
    this.deathReason = "";
    this.scoreState = createScoreState(START_Y);
    this.chambers.clear();
    this.surfaceByHandle.clear();
    this.destroyedHazards.clear();
    this.missedHazards.clear();
    this.collectedPowerups.clear();
    this.events.length = 0;

    this.world = new RAPIER.World({ x: 0, y: PHYSICS.gravity });
    this.world.timestep = FIXED_DT;
    this.eventQueue = new RAPIER.EventQueue(true);
        createContinuousWalls(this.world);

    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(START_X, START_Y)
      .lockRotations()
      .setCcdEnabled(true)
      .setCanSleep(false);
    this.tomatoBody = this.world.createRigidBody(bodyDesc);
    this.tomatoCollider = this.world.createCollider(
      RAPIER.ColliderDesc.ball(PULP_RADIUS).setMass(1).setRestitution(PHYSICS.pulpRestitution).setRestitutionCombineRule(RAPIER.CoefficientCombineRule.Max).setFriction(PHYSICS.pulpFriction).setFrictionCombineRule(RAPIER.CoefficientCombineRule.Min).setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS),
      this.tomatoBody,
    );

    this.ensureChambers(0);
  }

  setCoreRequested(requested: boolean): void {
    this.requestedCore = requested;
  }

  tick(): void {
    if (this.runState !== "playing") return;

    this.tickCount += 1;
    if (this.damageInvulnerabilityTicks > 0) this.damageInvulnerabilityTicks -= 1;
    if (this.pistonCooldown > 0) this.pistonCooldown -= 1;
    const overdriveWasActive = this.overdriveTicks > 0;
    if (this.requestedCore && !this.previousCoreRequested) this.pistonBufferTicks = 30;
    this.applyMaterialRequest();
    if (this.pistonBufferTicks > 0) {
      if (this.applyPistonKick()) this.pistonBufferTicks = 0;
      else this.pistonBufferTicks -= 1;
    }
    this.previousCoreRequested = this.requestedCore;

    const velocityBefore = this.tomatoBody.linvel();
    const depthBeforeStep = Math.max(0, this.scoreState.maxDepth - START_Y);
    const basePace = paceForDepth(depthBeforeStep);
    const pace = overdrivePaceForBase(basePace, this.overdriveTicks > 0, this.preOverdrivePace);
    const terminal = (this.material === "core" ? PHYSICS.coreTerminalSpeed : PHYSICS.pulpTerminalSpeed) * pace;
    const verticalDrag = (-PHYSICS.gravity * velocityBefore.y * Math.abs(velocityBefore.y)) / (terminal * terminal);
    const horizontalDrag = -velocityBefore.x * (this.material === "core" ? 0.12 : 0.7) / pace;
    this.tomatoBody.resetForces(false);
    this.tomatoBody.addForce({ x: horizontalDrag, y: verticalDrag }, true);

    this.world.step(this.eventQueue);
    const impactSpeed = Math.hypot(velocityBefore.x, velocityBefore.y);
    this.processCollisions(impactSpeed);
    this.updateSlidingRails();

    const position = this.tomatoBody.translation();
    scoreDepth(this.scoreState, position.y, scoreMultiplierForPatents(this.patentCount));
    this.ensureChambers(Math.floor(position.y / CHAMBER_HEIGHT));
    this.updateCameraAndSterilizer(position.y);
    if (this.runState !== "playing") return;
    this.checkPowerups(position.x, position.y);
    this.checkHazards(position.x, position.y);
    this.checkMissedHazards(position.y);
    if (overdriveWasActive) {
      this.overdriveTicks -= 1;
      if (this.overdriveTicks === 0) this.preOverdrivePace = null;
    }

    if (position.y > 100_000 || !Number.isFinite(position.x + position.y)) {
      this.kill("timeline-corruption", position.x, position.y);
    }
  }

  drainEvents(): GameEvent[] {
    if (this.events.length === 0) return [];
    return this.events.splice(0, this.events.length);
  }

  snapshot(): GameSnapshot {
    const position = this.tomatoBody.translation();
    const velocity = this.tomatoBody.linvel();
    const minY = this.cameraY - 80;
    const maxY = this.cameraY + VIEW_HEIGHT + 80;
    const segments = [...this.chambers.values()].flatMap(({ spec }) =>
      spec.segments
        .filter((segment) => (segment.ay <= maxY && segment.by >= minY) || (segment.by <= maxY && segment.ay >= minY))
        .map((segment) => ({ ...segment, offsetX: this.segmentOffset(segment).x, offsetY: this.segmentOffset(segment).y })),
    );
    const hazards = [...this.chambers.values()].flatMap(({ spec }) =>
      spec.hazards
        .filter((hazard) => !this.destroyedHazards.has(hazard.id))
        .map((hazard): RenderHazard => {
          const position = this.hazardPosition(hazard, this.tomatoBody.translation().x);
          return { ...hazard, renderX: position.x, renderY: position.y };
        })
        .filter((hazard) => hazard.renderY + hazard.height >= minY && hazard.renderY <= maxY),
    );
    const powerups = [...this.chambers.values()].flatMap(({ spec }) =>
      spec.powerups
        .filter((powerup) => powerup.y + powerup.radius >= minY && powerup.y - powerup.radius <= maxY)
        .map((powerup): RenderPowerup => ({ ...powerup, collected: this.collectedPowerups.has(powerup.id) })),
    );
    const chamberIndex = Math.max(0, Math.floor(position.y / CHAMBER_HEIGHT));
    const activeChamber = this.chambers.get(chamberIndex) ?? this.chambers.get(0);
    if (!activeChamber) throw new Error("No chamber loaded for snapshot");
    const depth = Math.max(0, this.scoreState.maxDepth - START_Y);

    return {
      state: this.runState,
      mode: this.runMode,
      tick: this.tickCount,
      seed: this.seedValue,
      eraSeed: this.eraSeedValue,
      era: activeChamber.spec.era,
      tomato: {
        x: position.x,
        y: position.y,
        vx: velocity.x,
        vy: velocity.y,
        radius: this.material === "core" || this.blockedExpansion ? CORE_RADIUS : PULP_RADIUS,
        material: this.material,
        blockedExpansion: this.blockedExpansion,
      },
      cameraY: this.cameraY,
      sterilizerY: this.sterilizerY,
      pace: overdrivePaceForBase(paceForDepth(depth), this.overdriveTicks > 0, this.preOverdrivePace),
      overdriveTicks: this.overdriveTicks,
      patentCount: this.patentCount,
      scoreMultiplier: scoreMultiplierForPatents(this.patentCount),
      score: this.scoreState.score,
      depth,
      combo: this.scoreState.combo,
      chamberLabel: activeChamber.spec.label,
      segments,
      hazards,
      powerups,
    };
  }

  private applyMaterialRequest(): void {
    if (this.requestedCore) {
      if (this.material !== "core" || this.blockedExpansion) {
        this.material = "core";
        this.blockedExpansion = false;
        this.configureTomatoCollider(CORE_RADIUS, PHYSICS.coreRestitution, PHYSICS.coreFriction);
        this.events.push({ type: "material", material: "core", blocked: false });
      }
      return;
    }

    if (this.material === "pulp" && !this.blockedExpansion) return;
    const position = this.tomatoBody.translation();
    const overlap = this.world.intersectionWithShape(
      position,
      0,
      new RAPIER.Ball(PULP_RADIUS),
      undefined,
      undefined,
      this.tomatoCollider,
      this.tomatoBody,
    );
    if (overlap) {
      if (!this.blockedExpansion) {
        this.blockedExpansion = true;
        this.events.push({ type: "material", material: "pulp", blocked: true });
      }
      return;
    }

    this.material = "pulp";
    this.blockedExpansion = false;
    this.configureTomatoCollider(PULP_RADIUS, PHYSICS.pulpRestitution, PHYSICS.pulpFriction);
    this.events.push({ type: "material", material: "pulp", blocked: false });
  }

  private applyPistonKick(): boolean {
    if (this.pistonCooldown > 0) return false;
    const velocity = this.tomatoBody.linvel();
    if (Math.hypot(velocity.x, velocity.y) > 90) return false;

    const position = this.tomatoBody.translation();
    let normalX = 0;
    let normalY = 0;
    let contacts = 0;
    this.world.intersectionsWithShape(
      position,
      0,
      new RAPIER.Ball(PULP_RADIUS + 5),
      (collider) => {
        const surface = this.surfaceByHandle.get(collider.handle);
        if (surface) {
          const dx = surface.spec.bx - surface.spec.ax;
          const dy = surface.spec.by - surface.spec.ay;
          const length = Math.hypot(dx, dy);
          if (length > 0) {
            let x = -dy / length;
            let y = dx / length;
            const midpointX = (surface.spec.ax + surface.spec.bx) / 2;
            const midpointY = (surface.spec.ay + surface.spec.by) / 2;
            if ((position.x - midpointX) * x + (position.y - midpointY) * y < 0) {
              x = -x;
              y = -y;
            }
            normalX += x;
            normalY += y;
            contacts += 1;
          }
        } else {
          normalX += position.x < START_X ? 1 : -1;
          contacts += 1;
        }
        return true;
      },
      undefined,
      undefined,
      this.tomatoCollider,
      this.tomatoBody,
    );
    if (contacts === 0) return false;

    normalX += Math.max(-0.65, Math.min(0.65, (START_X - position.x) / 120));
    let length = Math.hypot(normalX, normalY);
    if (length < 0.2) {
      normalX = position.x < START_X ? 1 : -1;
      normalY = -0.35;
      length = Math.hypot(normalX, normalY);
    }
    normalX /= length;
    normalY /= length;
    this.tomatoBody.applyImpulse({ x: normalX * 190, y: normalY * 190 }, true);
    this.pistonCooldown = 24;
    this.events.push({ type: "piston", x: position.x, y: position.y, normalX, normalY });
    return true;
  }
  private configureTomatoCollider(radius: number, restitution: number, friction: number): void {
    this.tomatoCollider.setRadius(radius);
    this.tomatoCollider.setMass(1);
    this.tomatoCollider.setRestitution(restitution);
    this.tomatoCollider.setFriction(friction);
    this.tomatoBody.wakeUp();
  }

  private processCollisions(impactSpeed: number): void {
    this.eventQueue.drainCollisionEvents((first, second, started) => {
      if (!started) return;
      const tomatoHandle = this.tomatoCollider.handle;
      const surfaceHandle = first === tomatoHandle ? second : second === tomatoHandle ? first : null;
      if (surfaceHandle === null) return;
      if (!this.surfaceByHandle.has(surfaceHandle)) return;
      const position = this.tomatoBody.translation();
      this.events.push({ type: "impact", material: this.material, speed: impactSpeed, x: position.x, y: position.y });
    });
  }

  private ensureChambers(currentIndex: number): void {
    const first = Math.max(0, currentIndex - 2);
    const last = currentIndex + PRELOAD_CHAMBERS;
    for (let index = first; index <= last; index += 1) {
      if (!this.chambers.has(index)) this.addChamber(generateChamber(this.seedValue, index, this.eraSeedValue));
    }

    for (const [index, chamber] of this.chambers) {
      if (index >= first) continue;
      for (const surface of chamber.surfaces) {
        this.world.removeCollider(surface.collider, false);
        this.surfaceByHandle.delete(surface.collider.handle);
      }
      this.chambers.delete(index);
    }
  }

  private addChamber(spec: ChamberSpec): void {
    const surfaces = spec.segments
          .filter((segment) => !segment.id.endsWith("wall-l") && !segment.id.endsWith("wall-r"))
          .map((segment) => {
            const collider = this.world.createCollider(colliderForSegment(segment));
            const runtime = { spec: segment, collider };
            this.surfaceByHandle.set(collider.handle, runtime);
            return runtime;
          });
    this.chambers.set(spec.index, { spec, surfaces });
  }

  private updateCameraAndSterilizer(playerY: number): void {
    const target = Math.max(0, playerY - CAMERA_TARGET_Y);
    this.cameraY += (target - this.cameraY) * 0.12;

    const depth = Math.max(0, this.scoreState.maxDepth - START_Y);
    const speed = sterilizerSpeedForDepth(depth);
    this.sterilizerY += speed * FIXED_DT;
    this.sterilizerY = Math.max(this.sterilizerY, this.scoreState.maxDepth - STERILIZER_MAX_LEAD);

    const radius = this.material === "pulp" && !this.blockedExpansion ? PULP_RADIUS : CORE_RADIUS;
    if (playerY - radius <= this.sterilizerY) this.kill("time-collapse", this.tomatoBody.translation().x, playerY);
  }

  private motionProgress(phase = 0, period = 240): number {
    const pacedPeriod = period / paceForDepth(this.scoreState.maxDepth - START_Y);
    const progress = ((this.tickCount + phase) % pacedPeriod) / pacedPeriod;
    return progress < 0.5 ? progress * 2 : 2 - progress * 2;
  }

  private segmentOffset(segment: SegmentSpec): { x: number; y: number } {
    if (segment.phase === undefined) return { x: 0, y: 0 };
    const wave = this.motionProgress(segment.phase, segment.period);
    return { x: wave * (segment.travelX ?? 0), y: wave * (segment.travelY ?? 0) };
  }

  private updateSlidingRails(): void {
    for (const chamber of this.chambers.values()) {
      for (const surface of chamber.surfaces) {
        if (surface.spec.phase === undefined) continue;
        const offset = this.segmentOffset(surface.spec);
        surface.collider.setTranslation({
          x: (surface.spec.ax + surface.spec.bx) / 2 + offset.x,
          y: (surface.spec.ay + surface.spec.by) / 2 + offset.y,
        });
      }
    }
  }

  private hazardPosition(hazard: HazardSpec, playerX: number): { x: number; y: number } {
    let x = hazard.x;
    let y = hazard.y;
    if (hazard.motion !== "static") {
      const wave = this.motionProgress(hazard.phase, hazard.period);
      if (hazard.motion === "horizontal") x += (wave - 0.5) * (hazard.travelX ?? 0);
      else if (hazard.motion === "vertical") y += (wave - 0.5) * (hazard.travelY ?? 0);
      else if (hazard.motion === "burst") {
        const burst = Math.pow(Math.max(0, Math.sin(wave * Math.PI * 2)), 3);
        x += (burst - 0.25) * (hazard.travelX ?? 0);
      } else {
        const tracking = Math.max(-(hazard.travelX ?? 0) / 2, Math.min((hazard.travelX ?? 0) / 2, (playerX - hazard.x - hazard.width / 2) * (hazard.reactivity ?? 0)));
        x += (wave - 0.5) * (hazard.travelX ?? 0) + tracking;
        y += (wave - 0.5) * (hazard.travelY ?? 0);
      }
    }
    return { x: Math.max(WALL_LEFT + 4, Math.min(WALL_RIGHT - hazard.width - 4, x)), y };
  }

  private checkPowerups(playerX: number, playerY: number): void {
    const playerRadius = this.material === "pulp" && !this.blockedExpansion ? PULP_RADIUS : CORE_RADIUS;
    for (const chamber of this.chambers.values()) {
      for (const powerup of chamber.spec.powerups) {
        if (this.collectedPowerups.has(powerup.id)) continue;
        if (Math.hypot(playerX - powerup.x, playerY - powerup.y) > playerRadius + powerup.radius) continue;
        this.collectedPowerups.add(powerup.id);
        if (powerup.kind === "regular-patent") {
          if (this.patentCount >= MAX_PATENTS) {
            this.scoreState.score += PATENT_MAX_BONUS_SCORE;
            this.events.push({ type: "patentCollected", x: powerup.x, y: powerup.y, count: this.patentCount, added: false });
          } else {
            this.patentCount = Math.min(MAX_PATENTS, this.patentCount + 1);
            this.events.push({ type: "patentCollected", x: powerup.x, y: powerup.y, count: this.patentCount, added: true });
          }
        } else {
          if (this.overdriveTicks <= 0) {
            const depth = Math.max(0, this.scoreState.maxDepth - START_Y);
            this.preOverdrivePace = paceForDepth(depth);
          }
          this.overdriveTicks = OVERDRIVE_TICKS;
          this.events.push({ type: "overdrive", x: powerup.x, y: powerup.y });
        }
        return;
      }
    }
  }

  private checkHazards(playerX: number, playerY: number): void {
    if (this.runState !== "playing") return;
    const chamberIndex = Math.max(0, Math.floor(playerY / CHAMBER_HEIGHT));
    const radius = this.material === "pulp" && !this.blockedExpansion ? PULP_RADIUS : CORE_RADIUS;
    for (let index = chamberIndex - 1; index <= chamberIndex + 1; index += 1) {
      const chamber = this.chambers.get(index);
      if (!chamber) continue;
      for (const hazard of chamber.spec.hazards) {
        if (this.destroyedHazards.has(hazard.id)) continue;
        const position = this.hazardPosition(hazard, playerX);
        if (!overlapsHazard(playerX, playerY, radius, hazard, position.x, position.y)) continue;
        if (canBreakHazard(this.material, hazard.weakness, this.overdriveTicks)) {
          this.destroyHazard(hazard, position.x, position.y, true);
          continue;
        }
        if (this.damageInvulnerabilityTicks > 0) continue;
        if (this.patentCount > 0) {
          this.patentCount -= 1;
          this.damageInvulnerabilityTicks = PATENT_DAMAGE_INVULNERABILITY_TICKS;
          resetCombo(this.scoreState);
          const hazardCenter = position.x + hazard.width / 2;
          const directionX = playerX === hazardCenter ? (hazard.id.length % 2 === 0 ? -1 : 1) : Math.sign(playerX - hazardCenter);
          this.events.push({ type: "patentLost", x: playerX, y: playerY, directionX, count: this.patentCount });
          continue;
        }
        this.kill(hazardDeathCause(hazard), playerX, playerY);
        return;
      }
    }
  }

  private checkMissedHazards(playerY: number): void {
    const playerX = this.tomatoBody.translation().x;
    for (const chamber of this.chambers.values()) {
      for (const hazard of chamber.spec.hazards) {
        if (this.destroyedHazards.has(hazard.id) || this.missedHazards.has(hazard.id)) continue;
        const position = this.hazardPosition(hazard, playerX);
        if (playerY <= position.y + hazard.height) continue;
        this.missedHazards.add(hazard.id);
        resetCombo(this.scoreState);
      }
    }
  }

  private destroyHazard(hazard: HazardSpec, x: number, y: number, rewardsCombo: boolean): void {
    if (this.destroyedHazards.has(hazard.id)) return;
    this.destroyedHazards.add(hazard.id);
    const points = rewardsCombo ? scorePlow(this.scoreState, scoreMultiplierForPatents(this.patentCount)) : 0;
    this.events.push({ type: "hazardBreak", x: x + hazard.width / 2, y: y + hazard.height / 2, combo: rewardsCombo ? this.scoreState.combo : 0, points });
  }

  private kill(cause: DeathCause, x: number, y: number): void {
    if (this.runState !== "playing") return;
    this.runState = "dead";
    resetCombo(this.scoreState);
    this.deathReason = deathMessageForCause(cause);
    this.events.push({ type: "death", cause, reason: this.deathReason, x, y });
  }
}
